/**
 * =============================================================================
 * Hook: useHeaderModuleFlags
 * Versão: 1.0.0 (2026-04-25)
 *
 * Descrição:
 *   Hook utilitário que centraliza a leitura das flags de módulos habilitados
 *   no tenant atual (CRM, Financeiro, Tarefas, Indicação) e a integração com
 *   `useActiveModule`. Retorna um objeto pronto para ser espalhado (spread)
 *   nos componentes <ModuleSwitcher /> e <HeaderOverflowMenu />.
 *
 *   FONTE ÚNICA DE VERDADE para visibilidade de módulos no header.
 *   Garante que TODOS os layouts (TenantLayout, CrmLayout, FinancialLayout,
 *   TasksLayout, ReferralLayout, ModularLayout) exponham as MESMAS flags,
 *   evitando regressões onde algum módulo (ex.: "Indicação") "some" ao trocar
 *   de contexto.
 * =============================================================================
 */
import { useAuth } from '@/hooks/useAuth';
import { useActiveModule, type ModuleType } from '@/hooks/useActiveModule';
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';
import { useReferralModuleAvailable } from '@/hooks/useReferralModuleAvailable';
import { useKnowledgePermissions } from '@/hooks/kb/useKnowledgePermissions';
// Submódulos de Tarefas (Retenção/Jurídico) — flags por tenant
import { useWorkflowModule } from '@/hooks/useWorkflowModule';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';

/**
 * Props padronizadas para <ModuleSwitcher /> e <HeaderOverflowMenu />.
 * Use spread (`{...moduleSwitcherProps}`) nos layouts para garantir consistência.
 */
export interface HeaderModuleSwitcherProps {
  activeModule: ModuleType;
  onSwitchModule: (module: ModuleType) => void;
  isCrmAvailable: boolean;
  isFinancialAvailable: boolean;
  isTasksAvailable: boolean;
  isReferralAvailable: boolean;
  /** Novo módulo "Atividades" (Fase 3 — Playbook de Vendas). */
  isActivitiesAvailable: boolean;
  /** Módulo Wiki / Base de Conhecimento (gate por permissão kb_view). */
  isKnowledgeAvailable: boolean;
}

interface UseHeaderModuleFlagsReturn {
  /** Módulo ativo (conversations | crm | financial | tasks | referral | activities). */
  activeModule: ModuleType;
  /** Função para alternar o módulo ativo. */
  switchModule: (module: ModuleType) => void;
  /** Flags individuais de disponibilidade (caso o consumidor precise delas isoladas). */
  isCrmAvailable: boolean;
  isFinancialAvailable: boolean;
  isTasksAvailable: boolean;
  isReferralAvailable: boolean;
  isActivitiesAvailable: boolean;
  isKnowledgeAvailable: boolean;
  /** Módulo Veículos — flag `vehicles_enabled` do tenant. */
  isVehiclesAvailable: boolean;
  /** Submódulo Retenção (tarefas + tenants.retention_enabled). */
  isRetentionAvailable: boolean;
  /** Submódulo Jurídico (tarefas + tenants.legal_enabled). */
  isLegalAvailable: boolean;
  /** Módulo Workflow — flag `workflow_enabled` do tenant (esteira comercial). */
  isWorkflowAvailable: boolean;
  /**
   * Objeto pronto para spread em <ModuleSwitcher /> e <HeaderOverflowMenu />.
   * Contém TODAS as props necessárias (incluindo isReferralAvailable e isActivitiesAvailable).
   */
  moduleSwitcherProps: HeaderModuleSwitcherProps;
}

/**
 * Lê as flags do tenant atual e centraliza a integração com useActiveModule.
 * Retorna `moduleSwitcherProps` pronto para ser espalhado nos componentes
 * de header, evitando esquecimento de algum módulo (ex.: Indicação, Atividades).
 */
export function useHeaderModuleFlags(): UseHeaderModuleFlagsReturn {
  const { currentTenant } = useAuth();

  // Flags vindas direto do tenant (defensivo: se não carregado, OFF).
  const crmEnabled = currentTenant?.crm_enabled ?? false;
  const financialEnabled = currentTenant?.financial_enabled ?? false;
  const tasksEnabled = currentTenant?.tasks_enabled ?? false;

  // Flag de Indicação (afiliados) — fonte única via hook dedicado.
  const { isAvailable: referralEnabled } = useReferralModuleAvailable();

  // Hook principal de módulo ativo: recebe as 4 flags em ordem fixa.
  const {
    activeModule,
    switchModule,
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
  } = useActiveModule(crmEnabled, financialEnabled, tasksEnabled, referralEnabled);

  // Módulo Atividades depende do CRM estar habilitado (a lista lê dados do CRM).
  // O gate fino por permissão (menu_key 'activities_list') é feito no ModuleSwitcher.
  const isActivitiesAvailable = isCrmAvailable;

  // Módulo Wiki / Base de Conhecimento — habilitado quando o usuário tem
  // permissão kb_view (admins recebem true automaticamente no hook) E o
  // tenant tem knowledge_enabled = true (feature flag / add-on futuro).
  const { canView: knowledgeCanView } = useKnowledgePermissions();
  const knowledgeModuleEnabled =
    (currentTenant as { knowledge_enabled?: boolean } | null)?.knowledge_enabled !== false;
  const isKnowledgeAvailable = knowledgeCanView && knowledgeModuleEnabled;

  // Módulo Veículos — fonte única: RPC get_vehicles_settings (flag do tenant).
  const isVehiclesAvailable = useVehiclesModuleEnabled();

  // Submódulos de Tarefas: só existem quando o módulo de Tarefas está ativo
  // E a flag específica do tenant está ligada (Configurações › Tarefas).
  const { retentionEnabled, legalEnabled } = useTasksModuleSettings();
  const isRetentionAvailable = isTasksAvailable && retentionEnabled;
  const isLegalAvailable = isTasksAvailable && legalEnabled;

  // Módulo Workflow — fonte única: RPC get_workflow_overview (flag do tenant).
  // A consulta é compartilhada via React Query, sem requisição extra por tela.
  const { isEnabled: isWorkflowAvailable } = useWorkflowModule();

  // Objeto consolidado para uso via spread nos layouts.
  const moduleSwitcherProps: HeaderModuleSwitcherProps = {
    activeModule,
    onSwitchModule: switchModule,
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
  };

  return {
    activeModule,
    switchModule,
    isCrmAvailable,
    isFinancialAvailable,
    isTasksAvailable,
    isReferralAvailable,
    isActivitiesAvailable,
    isKnowledgeAvailable,
    isVehiclesAvailable,
    isRetentionAvailable,
    isLegalAvailable,
    isWorkflowAvailable,
    moduleSwitcherProps,
  };
}
